// Supabase API 서비스 - 새로운 _renewed 테이블 사용
import { supabase } from '../lib/supabase';
import {
  BossInfo,
  BossSkill,
  BossEffect,
  BossSkillEffectRelation,
  ReferenceData,
  ApiResponse,
  BossFormData,
  SkillFormData,
  EffectFormData,
  TriggerFormData,
  CopyOptions,
  BossAnimation,
  AnimationFormData
} from '../types';

// ============================================
// 레이드 관련 API
// ============================================

export const raidApi = {
  // 모든 레이드 조회 (보스 포함)
  getAllWithBosses: async () => {
    const { data: raids, error: raidError } = await supabase
      .from('raid')
      .select('*')
      .order('min_entry_level', { ascending: true });
    
    if (raidError) {
      console.error('레이드 조회 에러:', raidError);
      throw new Error(raidError.message);
    }
    
    // 각 레이드에 속한 보스들 조회
    const { data: entities, error: entityError } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .not('spawn_raid_code', 'is', null)
      .in('entity_type', ['BOSS', 'BOSS_PHASE'])
      .order('spawn_order', { ascending: true });
    
    if (entityError) throw new Error(entityError.message);
    
    // BOSS_DOUBLE 타입도 포함
    const { data: doubleBosses, error: doubleError } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .not('spawn_raid_code', 'is', null)
      .eq('entity_type', 'BOSS_DOUBLE')
      .order('spawn_order', { ascending: true });
    
    if (doubleError) throw new Error(doubleError.message);
    
    const allBosses = [...(entities || []), ...(doubleBosses || [])];
    
    // 레이드별로 보스 그룹화
    const raidsWithBosses = (raids || []).map(raid => ({
      ...raid,
      bosses: allBosses.filter(entity => entity.spawn_raid_code === raid.raid_code)
    }));
    
    return raidsWithBosses;
  },
  
  // 모든 레이드 조회
  getAll: async () => {
    const { data, error } = await supabase
      .from('raid')
      .select('*')
      .order('min_entry_level', { ascending: true });
    
    if (error) throw new Error(error.message);
    return data || [];
  },
  
  // 특정 레이드의 보스 목록 조회
  getBossesByRaidCode: async (raidCode: string) => {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .eq('spawn_raid_code', raidCode)
      .in('entity_type', ['BOSS', 'BOSS_DOUBLE', 'BOSS_PHASE'])
      .order('spawn_order', { ascending: true });
    
    if (error) throw new Error(error.message);
    return data || [];
  },
  
  // 보스룸 목록 조회
  getBossRooms: async () => {
    const { data, error } = await supabase
      .from('boss_room')
      .select('*')
      .order('room_name', { ascending: true });
    
    if (error) throw new Error(error.message);
    return data || [];
  },
  
  // 보스룸 맵 데이터 조회
  getBossRoomMaps: async (roomCode: string) => {
    const { data, error } = await supabase
      .from('boss_rooms')
      .select('*')
      .eq('room_code', roomCode)
      .order('phase', { ascending: true });
    
    if (error) throw new Error(error.message);
    return data || [];
  },
  
  // 보스룸 맵 저장
  saveBossRoomMap: async (roomCode: string, phase: number, mapCode: string, metadata?: any) => {
    const { data, error } = await supabase
      .from('boss_rooms')
      .upsert({
        room_code: roomCode,
        phase: phase,
        map_code: mapCode,
        metadata: metadata || {}
      }, {
        onConflict: 'room_code,phase'
      })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },
  
  // 보스룸 맵 삭제
  deleteBossRoomMap: async (id: number) => {
    const { error } = await supabase
      .from('boss_rooms')
      .delete()
      .eq('id', id);
    
    if (error) throw new Error(error.message);
  },
  
  // 레이드 생성
  create: async (raid: any) => {
    const { data, error } = await supabase
      .from('raid')
      .insert(raid)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },
  
  // 레이드 수정
  update: async (raidCode: string, raid: any) => {
    const { data, error } = await supabase
      .from('raid')
      .update(raid)
      .eq('raid_code', raidCode)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },
  
  // 레이드 삭제
  delete: async (raidCode: string) => {
    const { error } = await supabase
      .from('raid')
      .delete()
      .eq('raid_code', raidCode);
    
    if (error) throw new Error(error.message);
  }
};

// ============================================
// 보스 관련 API
// ============================================

export const bossApi = {
  // 모든 보스 조회
  getAll: async (): Promise<BossInfo[]> => {
    const { data, error } = await supabase
      .from('boss_info_renewed')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw new Error(error.message);
    return data || [];
  },

  // 특정 보스 조회
  getById: async (bossCode: string): Promise<BossInfo | null> => {
    const { data, error } = await supabase
      .from('boss_info_renewed')
      .select('*')
      .eq('boss_code', bossCode)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw new Error(error.message);
    }
    return data;
  },

  // 새 보스 생성
  create: async (bossData: BossFormData): Promise<BossInfo> => {
    const { data, error } = await supabase
      .from('boss_info_renewed')
      .insert([bossData])
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 보스 수정
  update: async (bossCode: string, bossData: Partial<BossFormData>): Promise<BossInfo> => {
    const { data, error } = await supabase
      .from('boss_info_renewed')
      .update(bossData)
      .eq('boss_code', bossCode)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 보스 삭제
  delete: async (bossCode: string): Promise<void> => {
    const { error } = await supabase
      .from('boss_info_renewed')
      .delete()
      .eq('boss_code', bossCode);
    
    if (error) throw new Error(error.message);
  },
};

// ============================================
// 스킬 관련 API
// ============================================

export const skillApi = {
  // 특정 보스의 스킬 목록
  getByBoss: async (bossCode: string): Promise<BossSkill[]> => {
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .select('*')
      .eq('boss_code', bossCode)
      .order('order');
    
    if (error) throw new Error(error.message);
    return data || [];
  },

  // 특정 스킬 조회
  getById: async (skillKey: string): Promise<BossSkill | null> => {
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .select('*')
      .eq('key', skillKey)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }
    return data;
  },

  // 새 스킬 생성
  create: async (skillData: SkillFormData): Promise<BossSkill> => {
    // key 자동 생성
    if (!skillData.key) {
      const prefix = skillData.boss_code === 'COMMON' ? 'CMN' : skillData.boss_code;
      const timestamp = Date.now().toString(36).substring(-4);
      const sanitized = skillData.name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10);
      skillData.key = `${prefix}_${sanitized}_${timestamp}`.toUpperCase();
    }

    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .insert([skillData])
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 스킬 수정
  update: async (skillKey: string, skillData: Partial<SkillFormData>): Promise<BossSkill> => {
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .update(skillData)
      .eq('key', skillKey)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 스킬 삭제
  delete: async (skillKey: string): Promise<void> => {
    const { error } = await supabase
      .from('boss_skill_renewed')
      .delete()
      .eq('key', skillKey);
    
    if (error) throw new Error(error.message);
  },
};

// ============================================
// 효과 관련 API
// ============================================

export const effectApi = {
  // 특정 보스의 효과 목록
  getByBoss: async (bossCode: string): Promise<BossEffect[]> => {
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .select('*')
      .eq('boss_code', bossCode)
      .order('order');
    
    if (error) throw new Error(error.message);
    return data || [];
  },

  // 특정 효과 조회
  getById: async (effectKey: string): Promise<BossEffect | null> => {
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .select('*')
      .eq('key', effectKey)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }
    return data;
  },

  // 새 효과 생성
  create: async (effectData: EffectFormData): Promise<BossEffect> => {
    // key 자동 생성
    if (!effectData.key) {
      const prefix = effectData.boss_code === 'COMMON' ? 'CMN' : effectData.boss_code;
      const timestamp = Date.now().toString(36).substring(-4);
      const sanitized = effectData.name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10);
      effectData.key = `${prefix}_EFF_${sanitized}_${timestamp}`.toUpperCase();
    }

    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .insert([effectData])
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 효과 수정
  update: async (effectKey: string, effectData: Partial<EffectFormData>): Promise<BossEffect> => {
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .update(effectData)
      .eq('key', effectKey)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 효과 삭제
  delete: async (effectKey: string): Promise<void> => {
    const { error } = await supabase
      .from('boss_effect_renewed')
      .delete()
      .eq('key', effectKey);
    
    if (error) throw new Error(error.message);
  },
};

// ============================================
// 트리거 관련 API
// ============================================

// triggerApi는 제거되었습니다. improvedTriggerApi를 사용하세요.

// ============================================
// 스킬-효과 관계 API
// ============================================

export { skillEffectRelationApi } from "./skillEffectRelations";

// ============================================
// 트리거-효과 관계 API
// ============================================

// Deprecated - V2 트리거는 trigger_action_relation_v2 사용
/*
export const triggerEffectRelationApi = {
  // 특정 트리거의 효과 관계 목록
  getByTrigger: async (triggerId: string): Promise<BossTriggerEffectRelation[]> => {
    const { data, error } = await supabase
      .from('boss_trigger_effect_relation_renewed')
      .select('*')
      .eq('id', triggerId)
      .order('priority');
    
    if (error) throw new Error(error.message);
    return data || [];
  },

  // 새 관계 생성
  create: async (relationData: Partial<BossTriggerEffectRelation>): Promise<BossTriggerEffectRelation> => {
    const { data, error } = await supabase
      .from('boss_trigger_effect_relation_renewed')
      .insert([relationData])
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 관계 수정
  update: async (id: number, relationData: Partial<BossTriggerEffectRelation>): Promise<BossTriggerEffectRelation> => {
    const { data, error } = await supabase
      .from('boss_trigger_effect_relation_renewed')
      .update(relationData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 관계 삭제
  delete: async (id: number): Promise<void> => {
    const { error } = await supabase
      .from('boss_trigger_effect_relation_renewed')
      .delete()
      .eq('id', id);
    
    if (error) throw new Error(error.message);
  },

  // 트리거의 모든 효과 관계 삭제
  deleteByTrigger: async (triggerId: string): Promise<void> => {
    const { error } = await supabase
      .from('boss_trigger_effect_relation_renewed')
      .delete()
      .eq('id', triggerId);
    
    if (error) throw new Error(error.message);
  },
};
*/

// ============================================
// 트리거 API (제거됨 - V2 트리거 시스템 사용)
// ============================================
// triggerApi는 더 이상 사용하지 않습니다.
// improvedTriggerApi를 사용하세요.

// ============================================
// 참조 데이터 API
// ============================================

export const referenceApi = {
  // 모든 참조 데이터 한번에 로드 (수정된 버전)
  getAllReferenceData: async (): Promise<ReferenceData> => {
    // 🚨 기존 삭제된 테이블들 제거하고 새로운 뷰 사용
    const { data, error } = await supabase
      .from('v_all_reference_data')
      .select('*');
    
    if (error) throw new Error(error.message);
    
    // 뷰에서 온 데이터를 클라이언트가 기대하는 형태로 변환
    const referenceData: any = {};
    
    (data || []).forEach((item: any) => {
      referenceData[item.table_name] = item.data;
    });
    
    return {
      // 삭제된 테이블들은 빈 배열로 처리
      skillTypes: [], // inf_skill_type_renewed 삭제됨
      targetTypes: referenceData.target_types || [],
      skillAreas: referenceData.skill_areas || [],
      interruptibles: [], // inf_interruptible_renewed 삭제됨 (boolean 처리)
      effectTypes: [], // inf_effect_type_renewed 삭제됨
      effectAffects: referenceData.effect_affects || [],
      effectDispels: referenceData.effect_dispels || [],
      durationTypes: [], // inf_duration_type_renewed 삭제됨
      powerTypes: [], // inf_power_type_renewed 삭제됨
      stats: referenceData.stats || [],
      calculationMethods: referenceData.calculation_methods || [],
      difficulties: [], // inf_difficulty_renewed 삭제됨
      mechanismTypes: [] // inf_mechanism_type_renewed 삭제됨
    };
  },
};

// ============================================
// 유틸리티 API
// ============================================

export const utilityApi = {
  // 리소스 복사
  copyResource: async (options: CopyOptions): Promise<{ newKey: string }> => {
    // TODO: 복사 로직 구현
    throw new Error('Copy functionality not implemented yet');
  },

  // Key 생성
  generateKey: async (type: string, bossCode: string, name: string): Promise<string> => {
    const prefix = bossCode === 'COMMON' ? 'CMN' : bossCode;
    const typePrefix = {
      'skill': '',
      'effect': 'EFF_',
      'trigger': 'TRG_'
    }[type] || '';
    const sanitized = name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10);
    const timestamp = Date.now().toString(36).substring(-4);
    return `${prefix}_${typePrefix}${sanitized}_${timestamp}`.toUpperCase();
  },
};

// ============================================
// 애니메이션 관련 API
// ============================================

export const animationApi = {
  // 보스의 모든 애니메이션 조회
  getByBossCode: async (bossCode: string): Promise<BossAnimation[]> => {
    const { data, error } = await supabase
      .from('boss_animations_renewed')
      .select('*')
      .eq('boss_code', bossCode)
      .order('action_type', { ascending: true })
      .order('sort_order', { ascending: true });
    
    if (error) throw new Error(error.message);
    return data || [];
  },

  // 특정 액션의 모든 애니메이션 조회
  getByAction: async (bossCode: string, actionType: string): Promise<BossAnimation[]> => {
    const { data, error } = await supabase
      .from('boss_animations_renewed')
      .select('*')
      .eq('boss_code', bossCode)
      .eq('action_type', actionType)
      .order('sort_order', { ascending: true });
    
    if (error) throw new Error(error.message);
    return data || [];
  },

  // 애니메이션 생성
  create: async (animationData: AnimationFormData): Promise<BossAnimation> => {
    const { data, error } = await supabase
      .from('boss_animations_renewed')
      .insert({
        boss_code: animationData.boss_code,
        action_type: animationData.action_type,
        motion_type: animationData.motion_type,
        montage_name: animationData.montage_name,
        has_sound_effect: animationData.has_sound_effect,
        has_vfx: animationData.has_vfx,
        has_animation: animationData.has_animation,
        icon_url: animationData.icon_url,
        sort_order: animationData.sort_order || 0
      })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 애니메이션 업데이트
  update: async (id: number, animationData: Partial<AnimationFormData>): Promise<BossAnimation> => {
    const { data, error } = await supabase
      .from('boss_animations_renewed')
      .update({
        motion_type: animationData.motion_type,
        montage_name: animationData.montage_name,
        has_sound_effect: animationData.has_sound_effect,
        has_vfx: animationData.has_vfx,
        has_animation: animationData.has_animation,
        icon_url: animationData.icon_url,
        sort_order: animationData.sort_order
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  // 아이콘 업로드
  uploadIcon: async (file: File, bossCode: string, actionType: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${bossCode}_${actionType}_${Date.now()}.${fileExt}`;
    const filePath = `${bossCode}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('skill-icons')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('skill-icons')
      .getPublicUrl(filePath);

    return publicUrl;
  },

  // 아이콘 삭제
  deleteIcon: async (iconUrl: string): Promise<void> => {
    if (!iconUrl) return;

    // Extract file path from URL
    const url = new URL(iconUrl);
    const pathParts = url.pathname.split('/');
    const bucketIndex = pathParts.indexOf('skill-icons');
    if (bucketIndex === -1) return;

    const filePath = pathParts.slice(bucketIndex + 1).join('/');
    
    const { error } = await supabase.storage
      .from('skill-icons')
      .remove([filePath]);

    if (error) throw error;
  },

  // 단일 애니메이션 삭제
  delete: async (id: number): Promise<void> => {
    const { error } = await supabase
      .from('boss_animations_renewed')
      .delete()
      .eq('id', id);
    
    if (error) throw new Error(error.message);
  },

  // 특정 액션의 모든 애니메이션 삭제
  deleteByAction: async (bossCode: string, actionType: string): Promise<void> => {
    const { error } = await supabase
      .from('boss_animations_renewed')
      .delete()
      .eq('boss_code', bossCode)
      .eq('action_type', actionType);
    
    if (error) throw new Error(error.message);
  },

  // 보스의 모든 애니메이션 삭제
  deleteAllByBoss: async (bossCode: string): Promise<void> => {
    const { error } = await supabase
      .from('boss_animations_renewed')
      .delete()
      .eq('boss_code', bossCode);
    
    if (error) throw new Error(error.message);
  }
};